import { Box, Container, Divider, Theme, useMediaQuery, Tab } from '@mui/material';
import { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router';
import { useAuth } from 'oidc-react';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Header,
  LoadingScreen,
  Unauthenticated,
  UserManagement,
  TabDrawer,
  RoleManagement,
  ApiKeyManagement,
  ApplicationManagement,
  FeatureFlagsManagement,
  SalesText,
  Prices,
  ProductHierarchy,
  LanguageAssociation,
  Images,
  Configuration,
  Unauthorized,
  LanguageManagement,
  UnsupportedBrowser,
  PriceListMap,
  ProductCatalog,
  InternalProductCatalog,
  ProductConfiguration,
  MarketAssociation,
  MarketManagement,
  ArchivedConfiguration,
  DescopeListManagement,
  COEManagement,
  ProcosForPplManagement,
} from '.';

import { AppDispatch, AppState } from '../store';
import { useDispatch, useSelector } from 'react-redux';
import { Footer } from './Footer';
import { AlertMessage } from './Layouts/AlertMessage';
import { CommonDialog } from './Layouts/CommonDialog';
import { EModuleType, tabsList } from '../data/Constants';
import { useTranslation } from 'react-i18next';
import { NotificationManagement } from './Notification/Ace/NotificationManagement';
import { PropertyManagement } from './Product/Property/PropertyManagement';
import { PropertyAssociation } from './Product/Property/PropertyAssociation';
import { setPageSize } from '../store/states/ApplicationSettingsSlice';
import { getLoggedInUserDetails } from '../store/states/UserSlice';
import { TechnicalInfo } from './DataMaintenance/TechnicalInfo';
import { MultiNumericManagement } from './Features/MultiNumericFeature/MultiNumericManagement';
import { Logout } from './Logout';
import { QueueManagement } from '../components/Notification/Ace/QueueManagement';
import { ProductReleaseManagement } from './Notification/Ace/ProductReleaseManagement';
import { ProductVersionMappingManagement } from './Notification/Ace/ProductVersionMappingManagement';
import { LovManagement } from './Product/LovManagement';

/**
 * Main component to handle the layout at the top level.
 * Sets the different routes.
 * Sets the current page size in the applicationSettingsState
 * @returns {JSX.Element} a container with the UI components at top level
 */

const getMiddleContent = (
  token: string,
  userDetails,
  tabValue: string,
  handleChange,
  t: (value: string) => string
) => {
  if (!token) {
    return (
      <Routes>
        <Route path="/logout" element={<Logout />} />
        <Route path="*" element={<Unauthenticated />} />
      </Routes>
    );
  } else if (
    !userDetails ||
    Object.keys(userDetails).length === 0 ||
    !userDetails.name ||
    !userDetails.isAdmin ||
    userDetails.isService
  ) {
    //unauthorize user is not an admin or if user is a service user
    return (
      <Routes>
        <Route path="*" element={<Navigate to="/NotAuthorized" replace={true} />} />
        <Route path="/NotAuthorized" element={<Unauthorized />} />
      </Routes>
    );
  } else {
    return (
      <Routes>
        <Route path="/logout" element={<Logout />} />
        <Route
          path="/*"
          element={
            <TabContext value={tabValue}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList onChange={handleChange} aria-label="Tab Menu">
                  {tabsList.map((tb) => (
                    <Tab
                      className="text-capitalize"
                      label={t('labels.' + tb.id)}
                      key={tb.id}
                      value={tb.id}
                    />
                  ))}
                </TabList>
              </Box>

              <TabPanel value={EModuleType.Administration} className="p-0 h-100">
                <Box className="d-flex h-inherit">
                  <TabDrawer tabValue={tabValue} />
                  <Box className="middle-container">
                    <Routes>
                      <Route path="/NotAuthenticated" element={<Unauthenticated />} />

                      <Route
                        path="/signin/oidc"
                        element={<Navigate to="/users" replace={true} />}
                      />
                      <Route
                        path="/signin/oidc-silent"
                        element={<Navigate to="/users" replace={true} />}
                      />

                      <Route path="/" element={<UserManagement token={token} />} />

                      <Route path="/users" element={<UserManagement token={token} />} />
                      <Route path="/users/*" element={<Navigate to="/users" replace={true} />} />

                      <Route path="/roles" element={<RoleManagement token={token} />} />
                      <Route path="/roles/*" element={<Navigate to="/roles" replace={true} />} />

                      <Route path="/apikeys" element={<ApiKeyManagement token={token} />} />
                      <Route
                        path="/apikeys/*"
                        element={<Navigate to="/apikeys" replace={true} />}
                      />

                      <Route
                        path="/applications"
                        element={<ApplicationManagement token={token} />}
                      />
                      <Route
                        path="/applications/*"
                        element={<Navigate to="/applications" replace={true} />}
                      />

                      <Route path="*" element={<Navigate to="/users" replace={true} />} />
                    </Routes>
                  </Box>
                </Box>
              </TabPanel>

              <TabPanel value={EModuleType.Datamaintainance} className="p-0 h-100">
                <Box className="d-flex h-inherit">
                  <TabDrawer tabValue={tabValue} />
                  <Box className="middle-container">
                    <Routes>
                      <Route path="/salesTexts" element={<SalesText token={token} />} />

                      <Route path="/prices" element={<Prices token={token} />} />

                      <Route
                        path="/productHierarchy"
                        element={<ProductHierarchy token={token} />}
                      />

                      <Route path="/images" element={<Images token={token} />} />

                      <Route path="/priceListMap" element={<PriceListMap token={token} />} />

                      <Route path="/technicalInfo" element={<TechnicalInfo token={token} />} />

                      <Route path="*" element={<Navigate to="/salesTexts" replace={true} />} />
                    </Routes>
                  </Box>
                </Box>
              </TabPanel>

              <TabPanel value={EModuleType.Product} className="p-0 h-100">
                <Box className="d-flex h-inherit">
                  <TabDrawer tabValue={tabValue} />
                  <Box className="middle-container">
                    <Routes>
                      <Route path="/market" element={<MarketManagement token={token} />} />
                      <Route
                        path="/marketAssociation"
                        element={<MarketAssociation token={token} />}
                      />

                      <Route
                        path="/languageAssociation"
                        element={<LanguageAssociation token={token} />}
                      />
                      <Route path="/language" element={<LanguageManagement token={token} />} />

                      <Route path="/productCatalog" element={<ProductCatalog token={token} />} />
                      <Route
                        path="/internalProductCatalog"
                        element={<InternalProductCatalog token={token} />}
                      />
                      <Route
                        path="/productConfiguration"
                        element={<ProductConfiguration token={token} />}
                      />
                      <Route
                        path="/archivedConfiguration"
                        element={<ArchivedConfiguration token={token} />}
                      />
                      <Route path="/properties" element={<PropertyManagement token={token} />} />
                      <Route
                        path="/propertyAssociation"
                        element={<PropertyAssociation token={token} />}
                      />
                      <Route path="/listOfValues" element={<LovManagement />} />

                      <Route path="*" element={<Navigate to="/market" replace={true} />} />
                    </Routes>
                  </Box>
                </Box>
              </TabPanel>

              <TabPanel value={EModuleType.FeatureFlags} className="p-0 h-100">
                <Box className="d-flex h-inherit">
                  <TabDrawer tabValue={tabValue} />
                  <Box className="middle-container">
                    <Routes>
                      <Route
                        path="/featureFlags"
                        element={<FeatureFlagsManagement token={token} />}
                      />
                      <Route
                        path="/featureFlags/*"
                        element={<Navigate to="/featureFlags" replace={true} />}
                      />

                      <Route path="*" element={<Navigate to="/featureFlags" replace={true} />} />
                    </Routes>
                  </Box>
                </Box>
              </TabPanel>

              <TabPanel value={EModuleType.Feature} className="p-0 h-100">
                <Box className="d-flex h-inherit">
                  <TabDrawer tabValue={tabValue} />
                  <Box className="middle-container">
                    <Routes>
                      <Route path="/features" element={<MultiNumericManagement token={token} />} />
                      <Route path="*" element={<Navigate to="/features" replace={true} />} />
                    </Routes>
                  </Box>
                </Box>
              </TabPanel>

              <TabPanel value={EModuleType.Configuration}>
                <Routes>
                  <Route path="/configuration" element={<Configuration />} />
                  <Route path="*" element={<Navigate to="/configuration" replace={true} />} />
                </Routes>
              </TabPanel>

              <TabPanel value={EModuleType.Notifications} className="p-0 h-100">
                <Box className="d-flex h-inherit">
                  <TabDrawer tabValue={tabValue} />
                  <Box className="middle-container">
                    <Routes>
                      <Route
                        path="/notification"
                        element={<NotificationManagement token={token} />}
                      />
                      <Route path="/queue" element={<QueueManagement token={token} />} />
                      <Route
                        path="/productRelease"
                        element={<ProductReleaseManagement token={token} />}
                      />
                      <Route
                        path="/productVersionMapping"
                        element={<ProductVersionMappingManagement token={token} />}
                      />
                      <Route path="*" element={<Navigate to="/notification" replace={true} />} />
                    </Routes>
                  </Box>
                </Box>
              </TabPanel>

              <TabPanel value={EModuleType.Reports} className="p-0 h-100">
                <Box className="d-flex h-inherit">
                  <TabDrawer tabValue={tabValue} />
                  <Box className="middle-container">
                    <Routes>
                      <Route
                        path="/descopeList"
                        element={<DescopeListManagement token={token ?? ''} />}
                      />
                      <Route path="/coe" element={<COEManagement token={token ?? ''} />} />
                      <Route
                        path="/procosForPpl"
                        element={<ProcosForPplManagement token={token ?? ''} />}
                      />
                      <Route path="*" element={<Navigate to="/descopeList" replace={true} />} />
                    </Routes>
                  </Box>
                </Box>
              </TabPanel>
            </TabContext>
          }
        />
      </Routes>
    );
  }
};

export const AppRouter = () => {
  const { t } = useTranslation();
  const pageSize = useSelector((state: AppState) => state.applicationSettings.pageSize);
  const dispatch = useDispatch<AppDispatch>();
  const userDetails = useSelector((state: AppState) => state.user.details);

  const auth = useAuth();
  const token = auth.userData?.access_token;
  const currentPageSize = {
    isExtraSmall: useMediaQuery(
      (theme: Theme) => theme.breakpoints.up('xs') && theme.breakpoints.down('sm')
    ),
    isSmall: useMediaQuery(
      (theme: Theme) => theme.breakpoints.up('sm') && theme.breakpoints.down('md')
    ),
    isMedium: useMediaQuery(
      (theme: Theme) => theme.breakpoints.up('md') && theme.breakpoints.down('lg')
    ),
    isLarge: useMediaQuery(
      (theme: Theme) => theme.breakpoints.up('lg') && theme.breakpoints.down('xl')
    ),
    isExtraLarge: useMediaQuery((theme: Theme) => theme.breakpoints.up('xl')),
  };

  useEffect(() => {
    if (JSON.stringify(pageSize) === JSON.stringify(currentPageSize)) {
      return;
    }
    dispatch(setPageSize(currentPageSize));
  });

  const [tabValue, setTabValue] = useState<string>('administration');

  const handleChange = (...params: [React.BaseSyntheticEvent, string]) => {
    setTabValue(params[1]);
  };

  useEffect(() => {
    const currentTab = tabsList.find((tb) =>
      tb.childs.includes(globalThis.location.pathname.split('/')[1])
    );
    const windowLocation = globalThis.location.pathname.includes('configuration')
      ? 'configuration'
      : 'administration';
    setTabValue(currentTab ? currentTab.id : windowLocation);
  }, []);

  useEffect(() => {
    if (token) {
      dispatch(getLoggedInUserDetails({ token: token }));
    }
  }, [token]);

  return (
    <Container maxWidth={false} className="appRouter root">
      <Header setTabValue={setTabValue} />
      <Divider className="divider" />
      <Box className="appRouterContent">
        <Box sx={{ width: '100%', typography: 'body1' }}>
          {getMiddleContent(token, userDetails, tabValue, handleChange, t)}
        </Box>
      </Box>
      <LoadingScreen />
      <Footer />
      <AlertMessage />
      <CommonDialog />
      <UnsupportedBrowser />
    </Container>
  );
};
